import { CollapsibleSider } from '@/features/layout/_components';
import { useStackSiderClearWhenLocationChange } from '@/features/layout/_hooks/clear-of-sider';
import {
  type FC,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from 'react';
import { useCommentPageTransitionParamStates } from '../../hooks/page-transition-param-states';
import { useCommentStackSiderView } from '../../hooks/stack-sider-view';
import { PinpointContext } from '../pinpoint/PinpointProvider';
import { CommentFilter } from './CommentFilter';
import { CommentDetails } from './details/CommentDetails';
import { CommentDetailsHeader } from './details/CommentDetailsHeader';
import { CommentFormHeader } from './form/CommentFormHeader';
import { CommentFormProvider } from './form/CommentFormProvider';
import { CommentFormWrapper } from './form/CommentFormWrapper';
import { CommentSiderList } from './list/CommentSiderList';
import { CommentSiderListHeader } from './list/CommentSiderListHeader';

export const CommentCollapsibleSider: FC = () => {
  const stackSiderView = useCommentStackSiderView();

  useStackSiderClearWhenLocationChange({ siderType: 'comment' });

  const viewType = stackSiderView.lastItem?.config.viewType;
  const [{ commentId }, setPageTransitionParams] =
    useCommentPageTransitionParamStates();
  const pinpointContext = useContext(PinpointContext);

  const handleBack = useCallback(() => {
    switch (viewType) {
      case 'comment-details':
        if (commentId) {
          setPageTransitionParams({ commentId: null });
        }
        break;
      default:
        break;
    }
    stackSiderView.pop();
  }, [viewType, stackSiderView, setPageTransitionParams, commentId]);

  const headerRender = useMemo(() => {
    switch (viewType) {
      case 'comment-details':
        return <CommentDetailsHeader />;
      case 'comment-list':
        return <CommentSiderListHeader />;
      case 'comment-form':
        return <CommentFormHeader />;
      default:
        return null;
    }
  }, [viewType]);

  const contentRender = useMemo(() => {
    switch (viewType) {
      case 'comment-details':
        return <CommentDetails />;
      case 'comment-form':
        return <CommentFormWrapper />;
      case 'comment-list':
        return <CommentSiderList />;
      case 'comment-filter':
        return <CommentFilter />;

      default:
        return null;
    }
  }, [viewType]);

  useEffect(() => {
    if (pinpointContext && !stackSiderView.list.length) {
      pinpointContext.setPinpoint(null); // Prevent throwing error when development
    }
  }, [pinpointContext, stackSiderView.list.length]);

  const siderRender: ReactNode = useMemo(() => {
    return (
      <CollapsibleSider
        open={!!stackSiderView.lastItem}
        canBack={stackSiderView.list.length > 1}
        onBack={handleBack}
        headerRightContent={headerRender}
      >
        {contentRender}
      </CollapsibleSider>
    );
  }, [stackSiderView, headerRender, contentRender, handleBack]);

  switch (viewType) {
    case 'comment-form':
      return <CommentFormProvider>{siderRender}</CommentFormProvider>;
    default:
      return siderRender;
  }
};
